import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader2, Banknote, Building2, BookOpen } from "lucide-react";
import ReportPageWrap from "@/components/reports/ReportPageWrap";
import {
  PageHeader,
  Card,
  Pill,
  SectionTitle,
} from "@/components/layout/PageShell";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { useReportBranch } from "@/contexts/ReportBranchContext";

import api from "@/lib/api";
import { aed } from "@/lib/format";
import { printAccountBook, printDayBook } from "@/lib/bookPrint";
import { cn } from "@/lib/utils";
import { ReportKpiStrip } from "@/components/reports/ReportKpiStrip";
import { ReportTableCard } from "@/components/reports/ReportTableCard";
import ReportExportButtons from "@/components/reports/ReportExportButtons";

const ACCOUNT_TABS = [
  { id: "cash", label: "Cash Book", accountsPath: "/accounts/cash-book", reportsPath: "/reports/cash-book", icon: Banknote },
  { id: "bank", label: "Bank Book", accountsPath: "/accounts/bank-book", reportsPath: "/reports/bank-book", icon: Building2 },
  { id: "day", label: "Day Book", accountsPath: "/accounts/day-book", reportsPath: "/reports/day-book", icon: BookOpen },
];

const monthStart = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};

const today = () => new Date().toISOString().slice(0, 10);

export default function AccountBooks() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { branchId, allBranches, reportParams, canQuery } = useReportBranch();

  const inReportCenter = location.pathname.startsWith("/reports/");

  const tabs = useMemo(
    () =>
      ACCOUNT_TABS.map((tab) => ({
        ...tab,
        path: inReportCenter ? tab.reportsPath : tab.accountsPath,
      })),
    [inReportCenter],
  );

  const activeTab = useMemo(() => {
    if (location.pathname.includes("bank-book")) return "bank";
    if (location.pathname.includes("day-book")) return "day";
    return "cash";
  }, [location.pathname]);

  const [fromDate, setFromDate] = useState(monthStart);
  const [toDate, setToDate] = useState(today);
  const [dayDate, setDayDate] = useState(today);

  const isDayBook = activeTab === "day";

  const { data: report, isLoading, isFetching } = useQuery({
    queryKey: ["books", activeTab, branchId, fromDate, toDate, dayDate],
    queryFn: async () => {
      if (activeTab === "day") {
        const { data: res } = await api.get("/books/day", {
          params: { ...reportParams, date: dayDate },
        });
        return res;
      }
      const endpoint = activeTab === "bank" ? "/books/bank" : "/books/cash";
      const { data: res } = await api.get(endpoint, {
        params: { ...reportParams, from_date: fromDate, to_date: toDate },
      });
      return res;
    },
    enabled: Boolean(canQuery),
  });

  const title = tabs.find((t) => t.id === activeTab)?.label ?? "Account Books";

  const handlePrint = () => {
    if (isDayBook) printDayBook(report, title, { branchId, allBranches });
    else printAccountBook(report, title, { branchId, allBranches });
  };

  return (
    <ReportPageWrap>
      <PageHeader
        title={title}
        subtitle={
          isDayBook
            ? "All voucher lines posted on the selected date"
            : "Receipts and payments with running balance"
        }
        actions={
          <ReportExportButtons
            disabled={!report}
            title={title}
            filename={activeTab === "day" ? "day-book" : activeTab === "bank" ? "bank-book" : "cash-book"}
            printTestId="book-print"
            onPrint={handlePrint}
          />
        }
      />

      <div className="flex gap-1 mb-4 border-b border-slate-200">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => navigate(tab.path)}
              className={cn(
                "flex items-center gap-1.5 px-4 py-2.5 text-[12.5px] font-medium border-b-2 -mb-px transition-colors",
                activeTab === tab.id
                  ? "border-blue-600 text-blue-700"
                  : "border-transparent text-slate-600 hover:text-slate-900",
              )}
              data-testid={`book-tab-${tab.id}`}
            >
              <Icon size={14} /> {tab.label}
            </button>
          );
        })}
      </div>

      <Card className="p-5 mb-4">
        <SectionTitle className="!mb-3">Filters</SectionTitle>
        {isDayBook ? (
          <div className="max-w-xs">
            <Label className="text-[12px] text-slate-600 mb-1 block">Date</Label>
            <Input
              type="date"
              value={dayDate}
              onChange={(e) => setDayDate(e.target.value)}
              className="h-9"
              data-testid="book-day-date"
            />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-3 max-w-xl">
            <div>
              <Label className="text-[12px] text-slate-600 mb-1 block">From Date</Label>
              <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="h-9" />
            </div>
            <div>
              <Label className="text-[12px] text-slate-600 mb-1 block">To Date</Label>
              <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="h-9" />
            </div>
          </div>
        )}
      </Card>

      {report && !isDayBook && (
        <ReportKpiStrip
          items={[
            { label: "Opening", value: aed(report.opening_balance) },
            {
              label: "Receipts / Payments",
              value: `${aed(report.total_receipts)} / ${aed(report.total_payments)}`,
            },
            { label: "Closing", value: aed(report.closing_balance), emphasize: true },
          ]}
        />
      )}

      {report && isDayBook && (
        <ReportKpiStrip
          items={[
            { label: "Vouchers", value: report.voucher_count },
            {
              label: "Total Debit / Credit",
              value: `${aed(report.total_debit)} / ${aed(report.total_credit)}`,
            },
            {
              label: "Status",
              value: report.is_balanced ? "Balanced" : "Out of balance",
              emphasize: true,
            },
          ]}
        />
      )}

      <ReportTableCard
        title={isDayBook ? "Day Book Entries" : "Book Entries"}
        actions={
          <div className="flex items-center gap-2">
            {report?.entry_count != null && <Pill>{report.entry_count}</Pill>}
            {(isLoading || isFetching) && (
              <span className="text-[12px] text-slate-500 flex items-center gap-1.5">
                <Loader2 size={14} className="animate-spin" /> Loading…
              </span>
            )}
          </div>
        }
      >
        {!isDayBook ? (
          <table>
            <thead>
              <tr>
                <th className="text-left">Date</th>
                <th className="text-left">Voucher</th>
                <th className="text-left">Particulars</th>
                <th className="text-right">Receipt</th>
                <th className="text-right">Payment</th>
                <th className="text-right">Balance</th>
              </tr>
            </thead>
            <tbody>
              {report && (
                <tr className="bg-slate-50/60 italic text-slate-600">
                  <td colSpan={3}>Opening balance</td>
                  <td />
                  <td />
                  <td className="text-right font-mono font-semibold">{aed(report.opening_balance)}</td>
                </tr>
              )}
              {report?.entries?.map((e, i) => (
                <tr key={e.id} data-testid={`book-row-${i}`}>
                  <td>{e.voucher_date}</td>
                  <td className="font-mono text-blue-700">{e.voucher_number}</td>
                  <td>{e.particulars || "—"}</td>
                  <td className="text-right font-mono">{e.receipt > 0 ? aed(e.receipt) : ""}</td>
                  <td className="text-right font-mono">{e.payment > 0 ? aed(e.payment) : ""}</td>
                  <td className="text-right font-mono font-semibold">{aed(e.balance)}</td>
                </tr>
              ))}
            </tbody>
            {report && (
              <tfoot>
                <tr className="bg-slate-50 border-t font-semibold">
                  <td className="px-3 py-2" colSpan={3}>Closing</td>
                  <td className="text-right font-mono px-3 py-2">{aed(report.total_receipts)}</td>
                  <td className="text-right font-mono px-3 py-2">{aed(report.total_payments)}</td>
                  <td className="text-right font-mono text-blue-700 px-3 py-2">{aed(report.closing_balance)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        ) : (
          <table>
            <thead>
              <tr>
                <th className="text-left">Voucher</th>
                <th className="text-left">Type</th>
                <th className="text-left">Account</th>
                <th className="text-left">Particulars</th>
                <th className="text-right">Debit</th>
                <th className="text-right">Credit</th>
              </tr>
            </thead>
            <tbody>
              {report?.entries?.length === 0 && (
                <tr>
                  <td className="text-center text-slate-500 py-10" colSpan={6}>No vouchers on this date.</td>
                </tr>
              )}
              {report?.entries?.map((e, i) => (
                <tr key={e.id} data-testid={`day-row-${i}`}>
                  <td className="font-mono text-blue-700">{e.voucher_number}</td>
                  <td><Pill tone="gray">{e.voucher_type}</Pill></td>
                  <td>{e.account_code} — {e.account_name}</td>
                  <td>{e.narration || e.party_name || "—"}</td>
                  <td className="text-right font-mono">{e.debit > 0 ? aed(e.debit) : ""}</td>
                  <td className="text-right font-mono">{e.credit > 0 ? aed(e.credit) : ""}</td>
                </tr>
              ))}
            </tbody>
            {report?.entries?.length > 0 && (
              <tfoot>
                <tr className="bg-slate-50 border-t font-semibold">
                  <td className="text-right px-3 py-2" colSpan={4}>Totals</td>
                  <td className="text-right font-mono px-3 py-2">{aed(report.total_debit)}</td>
                  <td className="text-right font-mono px-3 py-2">{aed(report.total_credit)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        )}
      </ReportTableCard>
    </ReportPageWrap>
  );
}
